import React from "react";
import { ShieldCheck, Sparkles, Truck, ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/8271852f-8769-4308-a435-1104bfab838c/images/b4059d3059ed9e7924408d11440cfa5134c671204d4da51566225241904565de.png";

export default function Hero() {
  const { t } = useT();
  return (
    <section
      data-testid="hero-section"
      className="relative pt-24 lg:pt-28 pb-14 lg:pb-20 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Pristine clean shirts on a dry cleaning rack"
          className="h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/80 to-white/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7 fade-up">
          <div
            data-testid="veteran-badge"
            className="inline-flex items-center gap-2 rounded-full border border-brand-veteran/30 bg-white/80 backdrop-blur px-3.5 py-1.5 mb-6 shadow-sm"
          >
            <ShieldCheck className="h-4 w-4 text-brand-veteran" />
            <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-brand-veteran">
              {t("hero.badge")}
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-brand-navy">
            {t("hero.title1")}
            <span className="block text-brand-blue">{t("hero.title2")}</span>
            {t("hero.title3")}
          </h1>

          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
            {t("hero.subtitle")}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              asChild
              data-testid="hero-schedule-cta"
              className="rounded-full bg-brand-blue hover:bg-brand-navy text-white h-12 px-7 text-base font-semibold shadow-[0_12px_30px_-12px_rgba(2,132,199,0.7)]"
            >
              <a href="#schedule" className="inline-flex items-center gap-2">
                {t("cta.schedulePickupFree")} <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-services-cta"
              className="rounded-full h-12 px-7 text-base font-semibold border-slate-300 hover:border-brand-navy hover:bg-white"
            >
              <a href="#services">{t("cta.exploreServices")}</a>
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3">
            <div className="flex items-center gap-1.5">
              {["s1", "s2", "s3", "s4", "s5"].map((id) => (
                <Star
                  key={id}
                  className="h-4 w-4 fill-amber-400 stroke-amber-400"
                />
              ))}
              <span className="ml-1 text-sm font-semibold text-brand-navy">
                {t("hero.rating")}
              </span>
              <span className="text-sm text-slate-500">
                {t("hero.reviewsCount")}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Truck className="h-4 w-4 text-brand-blue" />
              {t("hero.freePickup")}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Sparkles className="h-4 w-4 text-brand-blue" />
              {t("hero.eco")}
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 lg:pl-6">
          <div className="relative">
            <div className="absolute -inset-3 bg-brand-lightblue/60 rounded-3xl blur-2xl" />

            {/* Floating brand seal — official logo */}
            <img
              data-testid="hero-logo-seal"
              src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/oxford_logo.jpg`}
              alt="Oxford Cleaners — Veteran and Family Owned"
              className="absolute -top-6 -right-2 sm:-top-7 lg:-top-8 lg:-right-4 h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24 rounded-2xl object-cover shadow-2xl ring-4 ring-white rotate-[6deg] z-20"
            />

            <div
              data-testid="hero-info-card"
              className="relative rounded-3xl bg-white/90 backdrop-blur-xl border border-slate-200 shadow-xl p-7 lg:p-8"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue">
                {t("hero.nowServing")}
              </p>
              <h3 className="font-display text-2xl font-bold text-brand-navy mt-2">
                {t("hero.eastValley")}
              </h3>
              <ul className="mt-5 grid grid-cols-2 gap-3">
                {["Chandler", "Maricopa", "Ahwatukee", "Tempe", "Gilbert"].map(
                  (c) => (
                    <li
                      key={c}
                      className="flex items-center gap-2 text-sm text-slate-700"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
                      {c}
                    </li>
                  ),
                )}
              </ul>
              <div className="mt-6 pt-5 border-t border-slate-200 text-sm text-slate-600">
                <p className="font-semibold text-brand-navy">
                  {t("hero.nextPickup")}
                </p>
                <p className="mt-1">{t("hero.nextPickupTime")}</p>
              </div>
              <Button
                asChild
                data-testid="hero-card-cta"
                className="mt-6 w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white h-11 font-semibold"
              >
                <a href="#schedule">{t("hero.reserveSpot")}</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
